import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "auth/Auth";
import { WEBSITES_API, formatApiError, sitePath } from "../utils/api";
import { isRealEstateSite, listingEntryPayload } from "../utils/vertical";

const CONFLICT_MESSAGE = "This website changed in another session. Reload and review the latest draft before saving.";

// Loads the tenant's websites and the selected site's domain, builder pages
// and what it publishes: trip packages (catalogue) for a travel site, CRM
// listings for a real estate site. Every mutation goes through here so revision guards and
// stale-response guards live in one place.
export default function useWebsiteSite() {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [domain, setDomainState] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [listings, setListings] = useState([]);
  const [builderPages, setBuilderPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const mountedRef = useRef(false);
  const loadTokenRef = useRef(0);
  const saveTokenRef = useRef(0);
  const siteIdRef = useRef(null);
  const sitesRef = useRef([]);

  const site = useMemo(() => sites.find((item) => item.id === siteId) || null, [sites, siteId]);
  const ready = Boolean(isAuthReady && isAuthenticated && axiosInstance);
  const isCurrent = useCallback((token) => mountedRef.current && token === loadTokenRef.current, []);

  const replaceSite = useCallback((nextSite) => {
    if (!nextSite) return;
    setSites((current) => {
      const next = current.some((item) => item.id === nextSite.id)
        ? current.map((item) => (item.id === nextSite.id ? nextSite : item))
        : [...current, nextSite];
      sitesRef.current = next;
      return next;
    });
  }, []);

  // `nextSite` is passed when the caller has it; otherwise it is looked up,
  // so the right publish list (catalogue or listings) is requested.
  const loadSiteDetails = useCallback(async (nextSiteId, token, nextSite) => {
    if (!nextSiteId) {
      setDomainState(null);
      setCatalogue([]);
      setListings([]);
      setBuilderPages(null);
      return;
    }
    const realEstate = isRealEstateSite(nextSite || sitesRef.current.find((item) => item.id === nextSiteId));
    const [publishResult, domainResult, pagesResult] = await Promise.allSettled([
      axiosInstance.get(sitePath(nextSiteId, realEstate ? "/listings" : "/catalogue")),
      axiosInstance.get(sitePath(nextSiteId, "/domain")),
      axiosInstance.get(sitePath(nextSiteId, "/builder/pages")),
    ]);
    if (!isCurrent(token)) return;
    const setPublishList = realEstate ? setListings : setCatalogue;
    (realEstate ? setCatalogue : setListings)([]);
    if (publishResult.status === "fulfilled") {
      setPublishList(publishResult.value.data || []);
    } else {
      setPublishList([]);
      setError(formatApiError(
        publishResult.reason,
        realEstate ? "The website listing list could not be loaded." : "The website package list could not be loaded.",
      ));
    }
    setDomainState(domainResult.status === "fulfilled" ? domainResult.value.data || null : null);
    setBuilderPages(pagesResult.status === "fulfilled" ? pagesResult.value.data || [] : null);
  }, [axiosInstance, isCurrent]);

  const reload = useCallback(async () => {
    if (!ready) return;
    const token = ++loadTokenRef.current;
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    try {
      const [templateResponse, sitesResponse] = await Promise.all([
        axiosInstance.get(`${WEBSITES_API}/templates`),
        axiosInstance.get(`${WEBSITES_API}/sites`),
      ]);
      if (!isCurrent(token)) return;
      const nextSites = sitesResponse.data || [];
      const preferred = nextSites.find((item) => item.id === siteIdRef.current) || nextSites[0] || null;
      setTemplates(templateResponse.data || []);
      setSites(nextSites);
      sitesRef.current = nextSites;
      setSiteId(preferred?.id || null);
      siteIdRef.current = preferred?.id || null;
      await loadSiteDetails(preferred?.id, token, preferred);
    } catch (requestError) {
      if (!isCurrent(token)) return;
      setLoadFailed(true);
      setError(formatApiError(requestError, "Website settings could not be loaded."));
    } finally {
      if (isCurrent(token)) setLoading(false);
    }
  }, [axiosInstance, isCurrent, loadSiteDetails, ready]);

  useEffect(() => {
    // Bumping both tokens on unmount invalidates any request still in flight.
    const loadTokens = loadTokenRef;
    const saveTokens = saveTokenRef;
    mountedRef.current = true;
    reload();
    return () => {
      mountedRef.current = false;
      loadTokens.current += 1;
      saveTokens.current += 1;
    };
  }, [reload]);

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated) {
      setSites([]);
      setSiteId(null);
      setTemplates([]);
      setDomainState(null);
      setCatalogue([]);
      setListings([]);
      setBuilderPages(null);
      setError(null);
      setLoadFailed(false);
    }
  }, [isAuthReady, isAuthenticated]);

  const selectSite = useCallback(async (nextSiteId) => {
    const token = ++loadTokenRef.current;
    siteIdRef.current = nextSiteId;
    setSiteId(nextSiteId);
    setError(null);
    setDomainState(null);
    setCatalogue([]);
    setListings([]);
    setBuilderPages(null);
    await loadSiteDetails(nextSiteId, token);
  }, [loadSiteDetails]);

  // Runs a site mutation with the save guard; returns the response data or null.
  const runSave = useCallback(async (request, fallbackMessage, { conflictMessage = CONFLICT_MESSAGE } = {}) => {
    if (!ready) return null;
    const token = ++saveTokenRef.current;
    setSaving(true);
    setError(null);
    try {
      const response = await request();
      if (!mountedRef.current || token !== saveTokenRef.current) return null;
      return response?.data ?? true;
    } catch (requestError) {
      if (!mountedRef.current || token !== saveTokenRef.current) return null;
      const status = requestError?.response?.status;
      if (status === 409 && conflictMessage) setError(conflictMessage);
      else if (status === 429) setNotice("Domain was checked a moment ago. Wait about 20 seconds, then check again.");
      else setError(formatApiError(requestError, fallbackMessage));
      return null;
    } finally {
      if (mountedRef.current && token === saveTokenRef.current) setSaving(false);
    }
  }, [ready]);

  const refreshSite = useCallback(async (id) => {
    try {
      const response = await axiosInstance.get(sitePath(id));
      if (mountedRef.current) replaceSite(response.data);
    } catch {
      // Non-fatal: the next reload picks it up.
    }
  }, [axiosInstance, replaceSite]);

  const refreshDomain = useCallback(async () => {
    const id = siteIdRef.current;
    if (!id || !axiosInstance) return;
    try {
      const response = await axiosInstance.get(sitePath(id, "/domain"));
      if (mountedRef.current && id === siteIdRef.current) setDomainState(response.data || null);
    } catch {
      // Keep the last known state; polling retries.
    }
  }, [axiosInstance]);

  const patch = useCallback(async (changes, successMessage = "Website changes saved") => {
    if (!site) return null;
    const data = await runSave(
      () => axiosInstance.patch(sitePath(site.id), { ...changes, expected_revision: site.revision }),
      "Website changes could not be saved.",
    );
    if (data && typeof data === "object") {
      replaceSite(data);
      if (successMessage) setNotice(successMessage);
      return data;
    }
    return null;
  }, [axiosInstance, replaceSite, runSave, site]);

  const setPublished = useCallback(async (published) => {
    if (!site) return null;
    const data = await runSave(
      () => axiosInstance.post(sitePath(site.id, published ? "/publish" : "/unpublish"), { expected_revision: site.revision }),
      published ? "The website could not be published." : "The website could not be unpublished.",
    );
    if (data && typeof data === "object") {
      replaceSite(data);
      setNotice(published ? "Website published" : "Website moved back to draft");
      return data;
    }
    return null;
  }, [axiosInstance, replaceSite, runSave, site]);

  const setDomain = useCallback(async (value) => {
    if (!site) return null;
    const data = await runSave(
      () => axiosInstance.put(sitePath(site.id, "/domain"), { domain: value }),
      "The domain could not be saved.",
      { conflictMessage: null },
    );
    if (data && typeof data === "object") {
      setDomainState(data);
      setNotice("Domain saved. Add the DNS records below, then check again.");
      refreshSite(site.id);
      return data;
    }
    return null;
  }, [axiosInstance, refreshSite, runSave, site]);

  const verifyDomain = useCallback(async () => {
    if (!site) return null;
    const data = await runSave(
      () => axiosInstance.post(sitePath(site.id, "/domain/verify")),
      "DNS could not be checked.",
      { conflictMessage: null },
    );
    if (data && typeof data === "object") {
      setDomainState(data);
      refreshSite(site.id);
      return data;
    }
    return null;
  }, [axiosInstance, refreshSite, runSave, site]);

  const removeDomain = useCallback(async () => {
    if (!site) return false;
    const data = await runSave(
      () => axiosInstance.delete(sitePath(site.id, "/domain")),
      "The domain could not be removed.",
      { conflictMessage: null },
    );
    if (!data) return false;
    setNotice("Custom domain removed");
    await refreshDomain();
    refreshSite(site.id);
    return true;
  }, [axiosInstance, refreshDomain, refreshSite, runSave, site]);

  const previewUrl = useCallback(async () => {
    if (!site) return null;
    try {
      const response = await axiosInstance.get(sitePath(site.id, "/preview-url"));
      return response.data?.url || null;
    } catch (requestError) {
      if (mountedRef.current) setError(formatApiError(requestError, "The preview link could not be created."));
      return null;
    }
  }, [axiosInstance, site]);

  // Publish settings for one CRM listing on a real estate site. The listing
  // endpoint's 409s carry their own reason (draft listing, slug in use), so
  // they are not reported as an edit conflict.
  const setListingEntry = useCallback(async (row, changes, successMessage = "Listing updated") => {
    if (!site) return null;
    const data = await runSave(
      () => axiosInstance.put(sitePath(site.id, `/listings/${row.listing_id}`), listingEntryPayload(row, changes)),
      "The listing could not be updated.",
      { conflictMessage: null },
    );
    if (data && typeof data === "object") {
      setListings((current) => current.map((item) => (item.listing_id === data.listing_id ? data : item)));
      if (successMessage) setNotice(successMessage);
      return data;
    }
    return null;
  }, [axiosInstance, runSave, site]);

  // siteId is the site being edited, so its own current or alias slug counts as available.
  const checkSlug = useCallback(async (slug, siteId) => {
    const params = siteId ? { slug, site_id: siteId } : { slug };
    const response = await axiosInstance.get(`${WEBSITES_API}/sites/slug-availability`, { params });
    return response.data;
  }, [axiosInstance]);

  // POST /sites, then PUT /domain when one was entered. Returns the new site.
  const createSite = useCallback(async (payload, customDomain) => {
    const created = await runSave(
      () => axiosInstance.post(`${WEBSITES_API}/sites`, payload),
      "The website could not be created.",
      { conflictMessage: null },
    );
    if (!created || typeof created !== "object") return null;
    const token = ++loadTokenRef.current;
    replaceSite(created);
    siteIdRef.current = created.id;
    setSiteId(created.id);
    let domainError = null;
    if (customDomain) {
      try {
        await axiosInstance.put(sitePath(created.id, "/domain"), { domain: customDomain });
      } catch (requestError) {
        domainError = formatApiError(requestError, "The domain could not be saved.");
      }
    }
    await loadSiteDetails(created.id, token, created);
    await refreshSite(created.id);
    if (!mountedRef.current) return created;
    if (domainError) setError(`Your website was created, but the domain was not saved: ${domainError} Add it again from the Domain tab.`);
    setNotice("Website created");
    return created;
  }, [axiosInstance, loadSiteDetails, refreshSite, replaceSite, runSave]);

  return {
    loading,
    loadFailed,
    saving,
    error,
    setError,
    notice,
    setNotice,
    templates,
    sites,
    site,
    selectSite,
    domain,
    catalogue,
    listings,
    setListingEntry,
    builderPages,
    reload,
    refreshDomain,
    patch,
    setPublished,
    setDomain,
    verifyDomain,
    removeDomain,
    previewUrl,
    checkSlug,
    createSite,
  };
}
